// Small reusable presentational pieces and the toast system.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Rag } from '../types'
import { useI18n } from '../i18n'
import { IconX } from './icons'

export function Spinner({ large }: { large?: boolean }) {
  return <div className={large ? 'spinner lg' : 'spinner'} role="status" aria-label="Loading" />
}

export function FullLoader() {
  return (
    <div className="full-loader">
      <Spinner large />
    </div>
  )
}

export function Avatar({ name, email, green, sm }: { name?: string; email?: string; green?: boolean; sm?: boolean }) {
  const initials = (name || email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('')
  return <div className={`avatar${green ? ' green' : ''}${sm ? ' sm' : ''}`}>{initials || '?'}</div>
}

export function RagDot({ rag }: { rag: Rag }) {
  return <span className={`rag-dot ${rag}`} />
}

// Tint a hex colour for use as a soft chip background.
function tint(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim())
  if (!m) return 'var(--purple-soft)'
  const n = parseInt(m[1], 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.16)`
}

// A soft, tinted chip naming the job role someone is covering.
export function RoleChip({ name, color }: { name: string; color?: string | null }) {
  const c = color || '#843A84'
  return (
    <span className="badge" style={{ background: tint(c), color: c }}>
      {name}
    </span>
  )
}

export function CoverageBar({ rag, pct }: { rag: Rag; pct: number }) {
  const cls = rag === 'none' ? 'green' : rag
  return (
    <div className="coverbar">
      <span className={cls} style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  )
}

export function Alert({ kind, children }: { kind: 'error' | 'ok' | 'info'; children: ReactNode }) {
  return <div className={`alert ${kind}`}>{children}</div>
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="big">{title}</div>
      {children}
    </div>
  )
}

export function Modal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string
  subtitle?: string
  onClose: () => void
  children: ReactNode
}) {
  const { t } = useI18n()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="between" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="sub">{subtitle}</p>}
          </div>
          <button className="iconbtn" onClick={onClose} aria-label={t('common.close')}>
            <IconX width={18} height={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

/* --------------------------------- Toasts -------------------------------- */

interface Toast {
  id: number
  message: string
  kind: 'ok' | 'error' | 'default'
}
interface ToastCtx {
  push: (message: string, kind?: Toast['kind']) => void
}
const ToastContext = createContext<ToastCtx | null>(null)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const push = useCallback((message: string, kind: Toast['kind'] = 'default') => {
    const id = Date.now() + Math.random()
    setToasts((t) => [...t, { id, message, kind }])
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3200)
  }, [])
  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind === 'default' ? '' : t.kind}`}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastCtx {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

/* ------------------------------ Confirm ------------------------------ */

interface ConfirmOptions {
  title?: string
  body?: string
  confirmLabel?: string
}
type ConfirmFn = (opts?: ConfirmOptions) => Promise<boolean>
const ConfirmContext = createContext<ConfirmFn | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((ok: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(o ?? {})
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok)
    resolver.current = null
    setOpts(null)
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <Modal title={opts.title ?? t('confirm.title')} onClose={() => settle(false)}>
          <p className="sub" style={{ marginTop: 4 }}>{opts.body ?? t('confirm.body')}</p>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => settle(true)}>
              {opts.confirmLabel ?? t('confirm.delete')}
            </button>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider')
  return ctx
}
